import React, { useState, useMemo, useEffect, useRef } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { cn } from "../lib/utils";

// 검색 가능한 콤보박스 (Popover + Input 조합, MUI Autocomplete 대체)
// options: { value, label }[] / value, onChange: 선택된 value / allLabel: "전체" 옵션 라벨(없으면 전체 옵션 미노출)
export function SearchableCombobox({
  label,
  value,
  onChange,
  options,
  placeholder,
  allLabel,
  width,
  triggerWidth = "var(--radix-popover-trigger-width)",
  align = "start",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const filteredOptions = useMemo(() => {
    if (!query) return options;
    return options.filter((o) => (o.label || "").toLowerCase().includes(query.toLowerCase()));
  }, [options, query]);

  // 키보드 탐색 시 allLabel까지 포함한 전체 옵션 목록 (0번 인덱스는 allLabel)
  const navItems = allLabel ? [{ value: "", label: allLabel }, ...filteredOptions] : filteredOptions;

  // 활성 옵션이 목록 스크롤 영역 밖으로 벗어나지 않도록 추적
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const activeEl = listRef.current.querySelector(`[data-nav-index="${activeIndex}"]`);
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const selectItem = (item) => {
    onChange(item.value);
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1 < navItems.length ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 >= 0 ? prev - 1 : navItems.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < navItems.length) {
        selectItem(navItems[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="flex flex-col gap-1" style={{ width }}>
      {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setQuery("");
            setActiveIndex(-1);
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-9 w-full items-center justify-start rounded-md border border-input bg-transparent px-3 py-1 text-left text-xs shadow-[0_1px_2px_0_rgb(0,0,0,0.05)] transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {selected ? (
              <span className="truncate">{selected.label}</span>
            ) : (
              <span className="text-muted-foreground truncate">{placeholder}</span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-2" style={{ width: triggerWidth }} align={align}>
          <Input
            autoFocus
            placeholder="검색..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            className="mb-2 h-8"
          />
          <div ref={listRef} className="max-h-60 overflow-auto flex flex-col">
            {allLabel && (
              <button
                type="button"
                data-nav-index={0}
                className={cn(
                  "text-left text-sm rounded-sm px-2 py-1.5 hover:bg-accent",
                  !value && "font-semibold",
                  activeIndex === 0 && "bg-accent"
                )}
                onClick={() => selectItem({ value: "", label: allLabel })}
              >
                {allLabel}
              </button>
            )}
            {filteredOptions.length === 0 ? (
              <div className="text-sm text-muted-foreground px-2 py-1.5">결과 없음</div>
            ) : (
              filteredOptions.map((o, idx) => {
                const navIndex = allLabel ? idx + 1 : idx;
                return (
                  <button
                    key={o.value}
                    type="button"
                    data-nav-index={navIndex}
                    className={cn(
                      "text-left text-sm rounded-sm px-2 py-1.5 hover:bg-accent",
                      value === o.value && "font-semibold",
                      activeIndex === navIndex && "bg-accent"
                    )}
                    onClick={() => selectItem(o)}
                  >
                    {o.label}
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
